import { createContext, useCallback, useContext, useState } from "react";
import { EventInterestedSheet } from "../components/EventInterestedSheet";
import { FloatingHearts } from "../components/FloatingHearts";
import { useEventInterested, useUnmarkInterested } from "./api/useEventRsvp";
import { useEventBuddies } from "./api/useEventBuddies";

type CelebrationCtx = {
  openSheet: (slug: string, celebrate: boolean) => void;
};

const InterestedCelebrationContext = createContext<CelebrationCtx>({
  openSheet: () => {},
});

type SheetState = {
  slug: string | null;
  isOpen: boolean;
  celebrate: boolean;
};

export function InterestedCelebrationProvider({ children }: { children: React.ReactNode }) {
  const [sheet, setSheet] = useState<SheetState>({ slug: null, isOpen: false, celebrate: false });
  const [heartsKey, setHeartsKey] = useState(0);

  const interested = useEventInterested(sheet.slug);
  const interestedItems = interested.data?.success ? (interested.data.data ?? []) : [];
  // data === undefined means the query hasn't resolved yet (covers the gap between
  // enabled:true and isFetching:true where isLoading briefly reads false in RQ v5)
  // Show skeletons while: no data yet, OR actively fetching with nothing to show yet.
  // The second condition covers the race where the list fetch returns empty before
  // the mark-interest mutation's onSettled invalidation triggers the refetch.
  const interestedLoading = !!sheet.slug && (
    interested.data === undefined || (interested.isFetching && interestedItems.length === 0)
  );
  const buddies = useEventBuddies(sheet.slug);
  const buddyItems = buddies.data?.success ? (buddies.data.data ?? []) : [];
  const buddiesLoading = !!sheet.slug && (
    buddies.data === undefined || (buddies.isFetching && buddyItems.length === 0)
  );
  const unmark = useUnmarkInterested(sheet.slug ?? "");

  const openSheet = useCallback((slug: string, celebrate: boolean) => {
    setSheet({ slug, isOpen: true, celebrate });
    if (celebrate) setHeartsKey((k) => k + 1);
  }, []);

  const close = useCallback(() => {
    setSheet((s) => ({ ...s, isOpen: false }));
  }, []);

  const handleUnmark = useCallback(() => {
    unmark.mutate(undefined, { onSettled: close });
  }, [unmark, close]);

  return (
    <InterestedCelebrationContext.Provider value={{ openSheet }}>
      {children}
      <FloatingHearts trigger={heartsKey} />
      <EventInterestedSheet
        isOpen={sheet.isOpen}
        celebrate={sheet.celebrate}
        onClose={close}
        onUnmark={handleUnmark}
        interestedItems={interestedItems}
        isLoadingItems={interestedLoading}
        buddyItems={buddyItems}
        isLoadingBuddies={buddiesLoading}
        slug={sheet.slug ?? ""}
      />
    </InterestedCelebrationContext.Provider>
  );
}

export function useInterestedCelebration() {
  return useContext(InterestedCelebrationContext);
}
